import { apiRequest, apiRequestBlob, apiUpload } from '@/lib/api/client';
import type { WorkspaceSummary } from '@/features/workspace/types';
import type { ModulePayload } from '@/features/workspace/types';
import type {
  AccessRequestAdmin,
  AdminAuditEntry,
  AdminUserCatalog,
  AdminUserDetail,
  AdminUserListOut,
  BulkResultOut,
  BulkRowIn,
  CreateUserOut,
  CreateUserPayload,
  InvitationOut,
  UserEditPayload,
} from '@/features/owner/adminUsersTypes';
import type {
  AuthorWorkspaceSummary,
  AuthorProfile,
  AuthorRequest,
  AuthorCoauthorAd,
  AuthorAdResponse,
  AuthorReadyManuscript,
  AuthorCoordinatorFeedback,
  AuthorAcademicConnection,
  AuthorConnectionMessage,
  AuthorCalendarOverview,
  AuthorCalendarEvent,
  AuthorResearchTool,
  AuthorResearchToolArtifact,
  AuthorStoredDocument,
  AuthorJointArticle,
  AuthorAIResponse,
} from '@/features/author/types';

export type OwnerDashboardRequest = {
  id: string;
  code: string;
  role: string;
  title: string;
  status: string;
  status_color: string;
  due_label: string;
};

export type OwnerDashboardThread = {
  initials: string;
  name: string;
  relation: string;
  preview: string;
  time_label: string;
  unread: number;
  color: string;
};

export type OwnerDashboardEvent = {
  id: string;
  title: string;
  when_label: string;
  meta: string;
  status: string;
  accent: string;
  to_path: string;
};

export type OwnerDashboard = {
  requests: OwnerDashboardRequest[];
  threads: OwnerDashboardThread[];
  events: OwnerDashboardEvent[];
  event_days: number[];
  notebook_stats: {
    active: number;
    drafts: number;
    starred: number;
    today: number;
  };
};

export type OwnerAuditEntry = {
  id: string;
  at: string;
  role: string;
  user: string;
  course: string;
  group: string;
  type: string;
  module: string;
  detail: string;
  project: string | null;
  privacy: string;
};

export type OwnerDecision = {
  id: string;
  title: string;
  summary: string;
  version: number;
  status: string;
  replaces_id: string | null;
  decided_at: string;
  author?: string | null;
  updated_at?: string | null;
};

export type OwnerMeeting = {
  id: string;
  kind: string;
  title: string;
  agenda: string;
  scheduled_at: string;
  recipient_label: string;
  invite_status: string;
  status: string;
  notify_in_app_delivered?: boolean;
  notify_email_delivered?: boolean;
  notify_email_to?: string | null;
  notify_detail?: string | null;
};

export type DoctoralMonitorItem = {
  id: string;
  name: string;
  supervisor: string;
  program: string;
  completionYear: number;
  defenseDate: string;
  defenseStatus: string;
  email: string;
  academic: string;
};

export type OwnerFileDocument = {
  id: string;
  title: string;
  bucket: string;
  version: number;
  mime_type: string;
  size_label: string;
  effective_at: string | null;
  change_note: string | null;
};

export const ownerApi = {
  workspace: () => apiRequest<WorkspaceSummary>('/owner/workspace', { channel: 'platform' }),
  dashboard: () => apiRequest<OwnerDashboard>('/owner/dashboard', { channel: 'platform' }),
  listAudit: () => apiRequest<OwnerAuditEntry[]>('/owner/audit', { channel: 'platform' }),
  module: (key: string, params?: { tab?: string; filter?: string; search?: string }) =>
    apiRequest<ModulePayload>(`/owner/modules/${key}`, {
      channel: 'platform',
      query: params,
    }),
  checkInvite: (email: string, locale = 'ru') =>
    apiRequest<{
      email: string;
      exists: boolean;
      can_invite: boolean;
      detail: string;
      code: string;
      user_id: string | null;
      invitation_status: string | null;
    }>('/owner/invite/check', { channel: 'platform', query: { email, locale } }),
  createAnnouncement: (body: { title: string; body: string; audience?: string }) =>
    apiRequest('/owner/announcements', { method: 'POST', body, channel: 'platform' }),
  listDoctoralMonitor: async () => {
    const rows = await apiRequest<
      Array<{
        id: string;
        name: string;
        supervisor: string;
        program: string;
        completion_year: number;
        defense_date: string;
        defense_status: string;
        email: string;
        academic_status: string;
      }>
    >('/owner/doctoral-monitor', { channel: 'platform' });
    return rows.map(
      (row): DoctoralMonitorItem => ({
        id: row.id,
        name: row.name,
        supervisor: row.supervisor,
        program: row.program,
        completionYear: row.completion_year,
        defenseDate: row.defense_date,
        defenseStatus: row.defense_status,
        email: row.email,
        academic: row.academic_status,
      }),
    );
  },
  createMeeting: (body: Record<string, unknown>) =>
    apiRequest<OwnerMeeting>('/owner/meetings', { method: 'POST', body, channel: 'platform' }),
  updateMeetingStatus: (id: string, status: string) =>
    apiRequest<OwnerMeeting>(`/owner/meetings/${id}/status`, {
      method: 'PATCH',
      body: { status },
      channel: 'platform',
    }),
  createDecision: (body: { title: string; summary: string }) =>
    apiRequest<OwnerDecision>('/owner/decisions', { method: 'POST', body, channel: 'platform' }),
  reviseDecision: (id: string, body?: { summary?: string }) =>
    apiRequest<OwnerDecision>(`/owner/decisions/${id}/revise`, {
      method: 'POST',
      body: body ?? {},
      channel: 'platform',
    }),
};

export const adminUsersApi = {
  catalog: () => apiRequest<AdminUserCatalog>('/owner/users/catalog', { channel: 'platform' }),
  list: (params?: { search?: string; category?: string; status?: string; sort?: string }) =>
    apiRequest<AdminUserListOut>('/owner/users', { channel: 'platform', query: params }),
  detail: (id: string) =>
    apiRequest<AdminUserDetail>(`/owner/users/${id}`, { channel: 'platform' }),
  create: (body: CreateUserPayload) =>
    apiRequest<CreateUserOut>('/owner/users', { method: 'POST', body, channel: 'platform' }),
  update: (id: string, body: UserEditPayload) =>
    apiRequest<AdminUserDetail>(`/owner/users/${id}`, {
      method: 'PATCH',
      body,
      channel: 'platform',
    }),
  setStatus: (id: string, body: { action: string; reason?: string }) =>
    apiRequest<AdminUserDetail>(`/owner/users/${id}/status`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  history: (id: string) =>
    apiRequest<AdminAuditEntry[]>(`/owner/users/${id}/history`, { channel: 'platform' }),
  sendMessage: (id: string, body: { subject: string; body: string }) =>
    apiRequest(`/owner/users/${id}/message`, { method: 'POST', body, channel: 'platform' }),
  invitation: (id: string) =>
    apiRequest<InvitationOut>(`/owner/users/${id}/invitation`, { channel: 'platform' }),
  resendInvitation: (id: string, locale = 'ru') =>
    apiRequest<InvitationOut>(`/owner/users/${id}/invitation/resend`, {
      method: 'POST',
      channel: 'platform',
      query: { locale },
    }),
  revokeInvitation: (id: string) =>
    apiRequest<InvitationOut>(`/owner/users/${id}/invitation/revoke`, {
      method: 'POST',
      channel: 'platform',
    }),
  bulkPrecheck: (rows: BulkRowIn[], locale?: string) =>
    apiRequest<BulkResultOut>('/owner/users/bulk/precheck', {
      method: 'POST',
      body: { rows, locale },
      channel: 'platform',
    }),
  bulkCommit: (rows: BulkRowIn[], locale?: string) =>
    apiRequest<BulkResultOut>('/owner/users/bulk/commit', {
      method: 'POST',
      body: { rows, locale },
      channel: 'platform',
    }),
  accessQueue: (status?: 'pending' | 'decided' | 'all') =>
    apiRequest<AccessRequestAdmin[]>(
      status
        ? `/owner/access-requests/queue?status_filter=${encodeURIComponent(status)}`
        : '/owner/access-requests/queue',
      { channel: 'platform' },
    ),
  approveAccess: (id: string, roles: string[], locale?: string) =>
    apiRequest<CreateUserOut>(`/owner/access-requests/${id}/approve`, {
      method: 'POST',
      body: { roles, locale },
      channel: 'platform',
    }),
  rejectAccess: (id: string, note?: string) =>
    apiRequest<AccessRequestAdmin>(`/owner/access-requests/${id}/reject`, {
      method: 'POST',
      body: { note },
      channel: 'platform',
    }),
};

export const advisorApi = {
  workspace: () => apiRequest<WorkspaceSummary>('/advisor/workspace', { channel: 'platform' }),
  module: (
    key: string,
    params?: { tab?: string; filter?: string; search?: string; candidate_id?: string },
  ) =>
    apiRequest<ModulePayload>(`/advisor/modules/${key}`, {
      channel: 'platform',
      query: params,
    }),
  reviewSection: (section: string, body: { student_id: string; decision: string; comment: string; target?: string; content_fingerprint: string }) =>
    apiRequest(`/doctoral/process/sections/${section}/decision`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  manualPoints: (body: {
    candidate_id: string;
    points: number;
    reason: string;
    evidence?: string;
  }) =>
    apiRequest('/advisor/points/manual', { method: 'POST', body, channel: 'platform' }),
};

export const supervisorApi = {
  workspace: () => apiRequest<WorkspaceSummary>('/supervisor/workspace', { channel: 'platform' }),
  module: (key: string, params?: { tab?: string; filter?: string; search?: string }) =>
    apiRequest<ModulePayload>(`/supervisor/modules/${key}`, {
      channel: 'platform',
      query: params,
    }),
  studentDetail: (studentId: string) =>
    apiRequest<Record<string, unknown>>(`/supervisor/students/${studentId}`, {
      channel: 'platform',
    }),
  updateStudent: (
    studentId: string,
    body: {
      topic?: string;
      review_status?: string;
      plan_status?: string;
      defence_status?: string;
      progress_pct?: number;
    },
  ) =>
    apiRequest(`/supervisor/students/${studentId}`, {
      method: 'PATCH',
      body,
      channel: 'platform',
    }),
  decideItem: (
    studentId: string,
    body: { collection: string; item_id: string; status: string },
  ) =>
    apiRequest<Record<string, unknown>>(`/supervisor/students/${studentId}/items`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  askStudent: (studentId: string) =>
    apiRequest<{ answer: string; suggestions: string[] }>(`/supervisor/students/${studentId}/ai`, {
      method: 'POST',
      channel: 'platform',
    }),
  addEntry: (
    studentId: string,
    body: { collection: string; title: string; kind?: string; note?: string; date?: string; deadline?: string; status_label?: string },
  ) =>
    apiRequest<Record<string, unknown>>(`/supervisor/students/${studentId}/entries`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  addLevelDocument: (
    studentId: string,
    body: { title: string; file_type?: string; file_name?: string },
  ) =>
    apiRequest<Record<string, unknown>>(`/supervisor/students/${studentId}/level-documents`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  replaceLevelDocument: (studentId: string, docId: string, fileName: string) =>
    apiRequest<Record<string, unknown>>(`/supervisor/students/${studentId}/level-documents/${docId}/replace`, {
      method: 'POST',
      body: { file_name: fileName },
      channel: 'platform',
    }),
  attachResearchFile: (studentId: string, itemId: string, fileName: string) =>
    apiRequest<Record<string, unknown>>(`/supervisor/students/${studentId}/research/${itemId}/file`, {
      method: 'POST',
      body: { file_name: fileName },
      channel: 'platform',
    }),
  deleteLevelDocument: (studentId: string, docId: string) =>
    apiRequest<Record<string, unknown>>(`/supervisor/students/${studentId}/level-documents/${docId}`, {
      method: 'DELETE',
      channel: 'platform',
    }),
  saveDraft: (
    studentId: string,
    body: {
      actuality?: string;
      goal?: string;
      plan_tasks?: string;
      conclusion?: string;
      bibliography?: string;
      translation_kz?: string;
      translation_ru?: string;
      translation_en?: string;
      consultant_name?: string;
      consultant_organization?: string;
      consultant_degree?: string;
      writing_checks?: Record<string, boolean>;
    },
  ) =>
    apiRequest<Record<string, unknown>>(`/supervisor/students/${studentId}/draft`, {
      method: 'PATCH',
      body,
      channel: 'platform',
    }),
  publishDraft: (studentId: string, revision: number) =>
    apiRequest<Record<string, unknown>>(`/supervisor/students/${studentId}/draft/publish`, {
      method: 'POST', body: { revision }, channel: 'platform',
    }),
  assignTopic: (studentId: string, topicId: string) =>
    apiRequest<Record<string, unknown>>(`/supervisor/students/${studentId}/topic/assign`, {
      method: 'POST', body: { topic_id: topicId }, channel: 'platform',
    }),
  decideModeration: (moderationId: string, body: { decision: 'APPROVED' | 'REJECTED'; note?: string }) =>
    apiRequest(`/supervisor/moderation/${moderationId}/decide`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  meetings: () =>
    apiRequest<import('@/features/student/types').MeetingConsultation[]>('/supervisor/meetings', {
      channel: 'platform',
    }),
  createMeeting: (body: { title: string; date: string; time: string; agenda?: string; to_role?: string; student_id?: string }) =>
    apiRequest<import('@/features/student/types').MeetingConsultation>('/supervisor/meetings', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
};

export const researcherApi = {
  workspace: () => apiRequest<WorkspaceSummary>('/researcher/workspace', { channel: 'platform' }),
  module: (key: string, params?: { tab?: string; filter?: string; search?: string }) =>
    apiRequest<ModulePayload>(`/researcher/modules/${key}`, {
      channel: 'platform',
      query: params,
    }),
};

export const operationsApi = {
  workspace: () =>
    apiRequest<WorkspaceSummary>('/operations/workspace', { channel: 'platform' }),
  module: (
    key: string,
    params?: { tab?: string; filter?: string; search?: string },
  ) =>
    apiRequest<Record<string, unknown>>(`/operations/modules/${key}`, {
      channel: 'platform',
      query: params,
    }),
  requestHistory: (code: string) =>
    apiRequest<
      Array<{
        id: string;
        request_code: string;
        actor_name: string;
        action: string;
        timestamp_label: string;
        note?: string;
      }>
    >(`/operations/requests/${code}/history`, { channel: 'platform' }),
  updateRequestStatus: (
    code: string,
    body: { status: string; stage?: string; note?: string },
  ) =>
    apiRequest(`/operations/requests/${code}/status`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  checkFile: (
    fileId: string,
    body: { status: string; completeness?: string; review_comment?: string },
  ) =>
    apiRequest(`/operations/files/${fileId}/check`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  readNotification: (notificationId: string) =>
    apiRequest(`/operations/notifications/${notificationId}/read`, {
      method: 'POST',
      channel: 'platform',
    }),
  readAllNotifications: () =>
    apiRequest('/operations/notifications/read-all', {
      method: 'POST',
      channel: 'platform',
    }),
};

export const scientificPartnerApi = {
  workspace: () =>
    apiRequest<WorkspaceSummary>('/scientific-partner/workspace', { channel: 'platform' }),
  module: (
    key: string,
    params?: { tab?: string; filter?: string; search?: string },
  ) =>
    apiRequest<Record<string, unknown>>(`/scientific-partner/modules/${key}`, {
      channel: 'platform',
      query: params,
    }),
  projectDetail: (code: string) =>
    apiRequest<Record<string, unknown>>(`/scientific-partner/projects/${code}`, {
      channel: 'platform',
    }),
  registerEvent: (code: string, registered: boolean) =>
    apiRequest<Record<string, unknown>>(`/scientific-partner/events/${code}/register`, {
      method: 'POST',
      body: { registered },
      channel: 'platform',
    }),
  answerPoll: (code: string, answer: string) =>
    apiRequest<Record<string, unknown>>(`/scientific-partner/polls/${code}/answer`, {
      method: 'POST',
      body: { answer },
      channel: 'platform',
    }),
  orderDetail: (code: string) =>
    apiRequest<Record<string, unknown>>(`/scientific-partner/orders/${code}`, {
      channel: 'platform',
    }),
  updateOrderStatus: (
    code: string,
    body: { status: string; next_step?: string; note?: string },
  ) =>
    apiRequest<Record<string, unknown>>(`/scientific-partner/orders/${code}/status`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  addOrderFile: (
    code: string,
    body: { filename: string; version_label: string; size_label?: string },
  ) =>
    apiRequest<Record<string, unknown>>(`/scientific-partner/orders/${code}/files`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  summary: () =>
    apiRequest<Record<string, unknown>>('/scientific-partner/workspace', { channel: 'platform' }),
  clientOrderDetail: (code: string) =>
    apiRequest<any>(`/scientific-partner/orders/${code}`, {
      channel: 'platform',
    }),
  updateClientOrderStatus: (
    code: string,
    body: { status: string; next_step?: string; note?: string },
  ) =>
    apiRequest<any>(`/scientific-partner/orders/${code}/status`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  submitPoll: (code: string, body: { answer: string }) =>
    apiRequest<Record<string, unknown>>(`/scientific-partner/polls/${code}/answer`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  createRequest: (body: {
    service_name?: string;
    topic?: string;
    title?: string;
    request_type?: string;
    description?: string;
    direction?: string;
    priority?: string;
    note?: string;
  }) =>
    apiRequest<Record<string, unknown>>('/scientific-partner/requests/create', {
      method: 'POST',
      body: {
        service_name: body.service_name || body.request_type || 'grant',
        topic: body.topic || body.title || 'Новая заявка',
        direction: body.direction,
        priority: body.priority || 'medium',
        note: body.note || body.description,
      },
      channel: 'platform',
    }),
  connections: (params?: { tab?: string; filter?: string; search?: string }) =>
    apiRequest<Record<string, unknown>>('/scientific-partner/connections', {
      channel: 'platform',
      query: params,
    }),
};

export const departmentApi = {
  summary: () =>
    apiRequest<any>('/department/summary', { channel: 'platform' }),
  monitoring: (params?: {
    search?: string;
    course?: number;
    supervisor?: string;
    program?: string;
    defense_status?: string;
    nird?: string;
    quick_filter?: string;
    academic_status?: string;
  }) =>
    apiRequest<any>('/department/monitoring', {
      channel: 'platform',
      query: params,
    }),
  staff: () =>
    apiRequest<any[]>('/department/staff', { channel: 'platform' }),
  tasks: () =>
    apiRequest<any[]>('/department/tasks', { channel: 'platform' }),
  createTask: (body: { title: string; recipient: string; deadline: string; priority?: string }) =>
    apiRequest<any>('/department/tasks', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  cycleTaskStatus: (taskId: string) =>
    apiRequest<any>(`/department/tasks/${taskId}/cycle-status`, {
      method: 'POST',
      channel: 'platform',
    }),
  surveys: () =>
    apiRequest<any[]>('/department/surveys', { channel: 'platform' }),
  surveyTemplates: () =>
    apiRequest<any[]>('/department/surveys/templates', { channel: 'platform' }),
  createSurvey: (body: { title: string; target_audience: string; deadline: string }) =>
    apiRequest<any>('/department/surveys', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  closeSurvey: (surveyId: string) =>
    apiRequest<any>(`/department/surveys/${surveyId}/close`, {
      method: 'POST',
      channel: 'platform',
    }),
  documents: () =>
    apiRequest<any[]>('/department/documents', { channel: 'platform' }),
  createDocument: (body: { filename: string; file_type: string; size_label?: string }) =>
    apiRequest<any>('/department/documents', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  replaceDocument: (docId: string, body: { filename?: string; file_type?: string; size_label?: string }) =>
    apiRequest<any>(`/department/documents/${docId}/replace`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  deleteDocument: (docId: string) =>
    apiRequest<{ ok: boolean }>(`/department/documents/${docId}`, {
      method: 'DELETE',
      channel: 'platform',
    }),
  events: () =>
    apiRequest<any[]>('/department/events', { channel: 'platform' }),
  createEvent: (body: { title: string; event_type?: string; event_date: string; participants: string }) =>
    apiRequest<any>('/department/events', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  toggleConfirmEvent: (eventId: string) =>
    apiRequest<any>(`/department/events/${eventId}/toggle-confirm`, {
      method: 'POST',
      channel: 'platform',
    }),
  aiQuery: (prompt: string) =>
    apiRequest<{ answer: string; suggestions: string[] }>('/department/ai-query', {
      method: 'POST',
      body: { prompt },
      channel: 'platform',
    }),
  enrollDoctoralStudent: (body: any) =>
    apiRequest<any>('/department/monitoring/enroll', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  updateDefenseStatus: (recordId: string, body: any) =>
    apiRequest<any>(`/department/monitoring/records/${recordId}/defense`, {
      method: 'PUT',
      body,
      channel: 'platform',
    }),
  verifyDiploma: (recordId: string, body: any) =>
    apiRequest<any>(`/department/monitoring/records/${recordId}/diploma-verify`, {
      method: 'PUT',
      body,
      channel: 'platform',
    }),
  updateEmployment: (recordId: string, body: any) =>
    apiRequest<any>(`/department/monitoring/records/${recordId}/employment`, {
      method: 'PUT',
      body,
      channel: 'platform',
    }),
  updateArchive: (recordId: string, body: any) =>
    apiRequest<any>(`/department/monitoring/records/${recordId}/archive`, {
      method: 'PUT',
      body,
      channel: 'platform',
    }),
  getDoctoralDossier: (recordId: string) =>
    apiRequest<any>(`/department/monitoring/records/${recordId}/dossier`, {
      channel: 'platform',
    }),
};

export const doctoralApi = {
  summary: () =>
    apiRequest<any>('/doctoral/summary', { channel: 'platform' }),
  overview: () =>
    apiRequest<any>('/doctoral/process/overview', { channel: 'platform' }),
  profile: () =>
    apiRequest<any>('/doctoral/process/profile', { channel: 'platform' }),
  updateProfile: (body: any) =>
    apiRequest<any>('/doctoral/process/profile', {
      method: 'PUT',
      body,
      channel: 'platform',
    }),
  topic: () =>
    apiRequest<any>('/doctoral/process/topic', { channel: 'platform' }),
  updateTopic: (body: any) =>
    apiRequest<any>('/doctoral/process/topic', {
      method: 'PUT',
      body,
      channel: 'platform',
    }),
  plan: () =>
    apiRequest<any>('/doctoral/process/plan', { channel: 'platform' }),
  updatePlan: (body: any) =>
    apiRequest<any>('/doctoral/process/plan', {
      method: 'PUT',
      body,
      channel: 'platform',
    }),
  nird: () =>
    apiRequest<any>('/doctoral/process/nird', { channel: 'platform' }),
  updateNird: (body: any) =>
    apiRequest<any>('/doctoral/process/nird/slot', {
      method: 'PUT',
      body,
      channel: 'platform',
    }),
  practices: () =>
    apiRequest<any[]>('/doctoral/process/practices', { channel: 'platform' }),
  updatePractice: (body: any) =>
    apiRequest<any>('/doctoral/process/practices', {
      method: 'PUT',
      body,
      channel: 'platform',
    }),
  internship: () =>
    apiRequest<any>('/doctoral/process/internship', { channel: 'platform' }),
  updateInternship: (body: any) =>
    apiRequest<any>('/doctoral/process/internship', {
      method: 'PUT',
      body,
      channel: 'platform',
    }),
  publications: () =>
    apiRequest<any[]>('/doctoral/process/publications', { channel: 'platform' }),
  createPublication: (body: any) =>
    apiRequest<any>('/doctoral/process/publications', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  achievements: () =>
    apiRequest<any[]>('/doctoral/process/achievements', { channel: 'platform' }),
  createAchievement: (body: any) =>
    apiRequest<any>('/doctoral/process/achievements', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  defense: () =>
    apiRequest<any>('/doctoral/process/defense', { channel: 'platform' }),
  completion: () =>
    apiRequest<any>('/doctoral/process/completion', { channel: 'platform' }),
  sources: () =>
    apiRequest<any>('/doctoral/process/enu-sources', { channel: 'platform' }),
  sectionState: (section: string) =>
    apiRequest<any>(`/doctoral/process/sections/${section}`, { channel: 'platform' }),
  saveSection: (section: string, saveKind: 'manual' | 'autosave' | 'submit') =>
    apiRequest<any>(
      saveKind === 'submit'
        ? `/doctoral/process/sections/${section}/submit`
        : `/doctoral/process/sections/${section}/save`,
      {
        method: 'POST',
        body: saveKind === 'submit' ? undefined : { save_kind: saveKind },
        channel: 'platform',
      },
    ),
  uploadTopicFile: (slot: string, file: File) =>
    apiUpload<any>(`/doctoral/process/sections/topic/files/${slot}`, file),
  openDoctoralFile: async (path: string, disposition: 'inline' | 'attachment') => {
    const result = await apiRequestBlob(path, { channel: 'platform', query: { disposition } });
    const url = URL.createObjectURL(result.blob);
    window.open(url, '_blank', 'noopener');
  },
  trajectory: () =>
    apiRequest<any>('/doctoral/trajectory', { channel: 'platform' }),
  collaborations: () =>
    apiRequest<any[]>('/doctoral/collaborations', { channel: 'platform' }),
  createCollaboration: (body: any) =>
    apiRequest<any>('/doctoral/collaborations', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  auditLogs: () =>
    apiRequest<any[]>('/doctoral/audit-logs', { channel: 'platform' }),
  reportProblem: (body: { topic: string; message: string }) =>
    apiRequest<any>('/doctoral/problem-report', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  aiQuery: (prompt: string) =>
    apiRequest<{ answer: string; suggestions: string[] }>('/doctoral/ai-query', {
      method: 'POST',
      body: { prompt },
      channel: 'platform',
    }),
  record: () =>
    apiRequest<any>('/doctoral/record', { channel: 'platform' }),
  updatePlannedDefense: (body: { planned_defense_date: string }) =>
    apiRequest<any>('/doctoral/planned-defense', {
      method: 'PUT',
      body,
      channel: 'platform',
    }),
  updateDiploma: (body: {
    diploma_file_id?: string;
    diploma_number?: string;
    diploma_issue_date?: string;
    diploma_verification_status?: string;
  }) =>
    apiRequest<any>('/doctoral/diploma', {
      method: 'PUT',
      body,
      channel: 'platform',
    }),
  updateEmployment: (body: {
    employment_status?: string;
    employer?: string;
    position?: string;
    city_country?: string;
    employment_start_date?: string;
    work_obligation?: string;
    employment_evidence_file_id?: string;
  }) =>
    apiRequest<any>('/doctoral/employment', {
      method: 'PUT',
      body,
      channel: 'platform',
    }),
  updateArchive: (body: {
    archive_status?: string;
    archive_completed_at?: string;
    archive_files_count?: number;
  }) =>
    apiRequest<any>('/doctoral/archive', {
      method: 'PUT',
      body,
      channel: 'platform',
    }),
  dossier: () =>
    apiRequest<any>('/doctoral/dossier', { channel: 'platform' }),
};

export const supervisedStudentApi = {
  uploadLevelDocument: (level: string, file: File, docId?: string) =>
    apiUpload<import('@/features/student/types').DocumentSample[]>(`/supervised-student/level-documents/${level}${docId ? `/${encodeURIComponent(docId)}/replace` : ''}`, file),
  deleteLevelDocument: (level: string, docId: string) =>
    apiRequest<import('@/features/student/types').DocumentSample[]>(`/supervised-student/level-documents/${level}/${encodeURIComponent(docId)}`, { method: 'DELETE', channel: 'platform' }),
  readLevelDocument: (path: string, disposition: 'inline' | 'attachment') =>
    apiRequestBlob(path, { channel: 'platform', query: { disposition } }),
  workspace: () =>
    apiRequest<any>('/supervised-student/workspace', { channel: 'platform' }),
  scientificWork: (level?: string) =>
    apiRequest<any>(level ? `/supervised-student/scientific-work?level=${encodeURIComponent(level)}` : '/supervised-student/scientific-work', { channel: 'platform' }),
  updateTopic: (body: any) =>
    apiRequest<any>('/supervised-student/topic', {
      method: 'PUT',
      body,
      channel: 'platform',
    }),
  submitCustomTopic: (body: any) =>
    apiRequest<any>('/supervised-student/topic/custom', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  updatePlan: (body: any) =>
    apiRequest<any>('/supervised-student/plan', {
      method: 'PUT',
      body,
      channel: 'platform',
    }),
  submitTask: (taskId: string, body: any) =>
    apiRequest<any>(`/supervised-student/tasks/${taskId}/submit`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  listLiterature: () =>
    apiRequest<any[]>('/supervised-student/literature', { channel: 'platform' }),
  addLiterature: (body: any) =>
    apiRequest<any>('/supervised-student/literature', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  submitWritingBlock: (blockKey: string, body: any) =>
    apiRequest<any>(`/supervised-student/writing/${blockKey}`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  submitResearch: (body: any) =>
    apiRequest<any>('/supervised-student/research', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  meetings: () =>
    apiRequest<any[]>('/supervised-student/meetings', { channel: 'platform' }),
  createMeeting: (body: { title: string; date: string; time: string; agenda?: string; to_role?: string }) =>
    apiRequest<any>('/supervised-student/meetings', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  respondMeeting: (meetingId: string, status: string) =>
    apiRequest<any>(`/supervised-student/meetings/${meetingId}/respond`, {
      method: 'POST',
      body: { status },
      channel: 'platform',
    }),
  saveTranslations: (body: { kz: string; ru: string; en: string }) =>
    apiRequest<any>('/supervised-student/translations', {
      method: 'PUT',
      body,
      channel: 'platform',
    }),
  answerTopicQuestion: (questionId: string, answer: string) =>
    apiRequest<any>(`/supervised-student/topic/questions/${questionId}/answer`, {
      method: 'POST',
      body: { answer },
      channel: 'platform',
    }),
  sendWorkMessage: (text: string) =>
    apiRequest<any>('/supervised-student/messages', {
      method: 'POST',
      body: { text },
      channel: 'platform',
    }),
  askAi: (prompt: string, context_type: string) =>
    apiRequest<{ answer: string; suggestions: string[] }>('/supervised-student/ai-query', {
      method: 'POST',
      body: { prompt, context_type },
      channel: 'platform',
    }),
  requests: (kind?: string) =>
    apiRequest<any[]>(kind ? `/supervised-student/requests?kind=${encodeURIComponent(kind)}` : '/supervised-student/requests', { channel: 'platform' }),
  createRequest: (body: any) =>
    apiRequest<any>('/supervised-student/requests', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  literatureSearch: (body: any) =>
    apiRequest<any>('/supervised-student/literature-search', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  tools: () =>
    apiRequest<any[]>('/supervised-student/tools', { channel: 'platform' }),
  createSurvey: (body: any) =>
    apiRequest<any>('/supervised-student/tools/surveys', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  project: () =>
    apiRequest<any>('/supervised-student/projects', { channel: 'platform' }),
  researchProjects: () =>
    apiRequest<any[]>('/supervised-student/research-projects', { channel: 'platform' }),
  projectAction: (projectId: string, body: any) =>
    apiRequest<any>(`/supervised-student/research-projects/${projectId}/action`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  submitProjectResult: (projectId: string, body: any) =>
    apiRequest<any>(`/supervised-student/projects/${projectId}/submit-result`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  opportunities: () =>
    apiRequest<any[]>('/supervised-student/opportunities', { channel: 'platform' }),
  trackOpportunity: (oppId: string, isTracked: boolean) =>
    apiRequest<any>(`/supervised-student/opportunities/${oppId}/track`, {
      method: 'POST',
      body: { is_tracked: isTracked },
      channel: 'platform',
    }),
  duties: () =>
    apiRequest<any[]>('/supervised-student/duties', { channel: 'platform' }),
  respondDuty: (dutyId: string, status: string) =>
    apiRequest<any>(`/supervised-student/duties/${dutyId}/respond`, {
      method: 'POST',
      body: { status },
      channel: 'platform',
    }),
  files: () =>
    apiRequest<any[]>('/supervised-student/files', { channel: 'platform' }),
  enuSources: () =>
    apiRequest<any[]>('/supervised-student/enu-sources', { channel: 'platform' }),
  aiQuery: (prompt: string, contextType?: string) =>
    apiRequest<{ answer: string; suggestions: string[] }>('/supervised-student/ai-query', {
      method: 'POST',
      body: { prompt, context_type: contextType },
      channel: 'platform',
    }),
};

export const authorApi = {
  workspace: () =>
    apiRequest<AuthorWorkspaceSummary>('/author/workspace', { channel: 'platform' }),
  getProfile: () =>
    apiRequest<AuthorProfile>('/author/profile', { channel: 'platform' }),
  updateProfile: (body: Partial<AuthorProfile>) =>
    apiRequest<AuthorProfile>('/author/profile', {
      method: 'PUT',
      body,
      channel: 'platform',
    }),
  getNotebook: () =>
    apiRequest<{ content: string }>('/author/notebook', { channel: 'platform' }),
  saveNotebook: (content: string) =>
    apiRequest<{ content: string }>('/author/notebook', {
      method: 'PUT',
      body: { content },
      channel: 'platform',
    }),
  getRequests: (params?: { work_option?: string; is_submitted?: boolean }) =>
    apiRequest<AuthorRequest[]>('/author/requests', {
      channel: 'platform',
      query: params
        ? {
            work_option: params.work_option,
            is_submitted: params.is_submitted !== undefined ? String(params.is_submitted) : undefined,
          }
        : undefined,
    }),
  getRequest: (id: string) =>
    apiRequest<AuthorRequest>(`/author/requests/${id}`, { channel: 'platform' }),
  createRequest: (body: Partial<AuthorRequest>) =>
    apiRequest<AuthorRequest>('/author/requests', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  autoSaveRequest: (body: Partial<AuthorRequest>) =>
    apiRequest<AuthorRequest>('/author/requests/auto-save', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  getAds: (params?: { subject_area?: string; target_database?: string }) =>
    apiRequest<AuthorCoauthorAd[]>('/author/ads', {
      channel: 'platform',
      query: params as Record<string, string | number | undefined>,
    }),
  createAd: (body: Partial<AuthorCoauthorAd>) =>
    apiRequest<AuthorCoauthorAd>('/author/ads', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  respondToAd: (adId: string, body: Partial<AuthorAdResponse>) =>
    apiRequest<AuthorAdResponse>(`/author/ads/${adId}/respond`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  getReadyManuscripts: () =>
    apiRequest<AuthorReadyManuscript[]>('/author/ready-manuscripts', { channel: 'platform' }),
  expressManuscriptInterest: (manuscriptId: string, notes?: string) =>
    apiRequest<AuthorReadyManuscript>(`/author/ready-manuscripts/${manuscriptId}/interest`, {
      method: 'POST',
      body: { notes },
      channel: 'platform',
    }),
  submitFeedback: (body: Partial<AuthorCoordinatorFeedback>) =>
    apiRequest<AuthorCoordinatorFeedback>('/author/feedback', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  getConnections: () =>
    apiRequest<AuthorAcademicConnection[]>('/author/connections', { channel: 'platform' }),
  getConnectionMessages: (connectionId: string) =>
    apiRequest<AuthorConnectionMessage[]>(`/author/connections/${connectionId}/messages`, {
      channel: 'platform',
    }),
  sendConnectionMessage: (connectionId: string, body: { text: string; attachment_url?: string }) =>
    apiRequest<AuthorConnectionMessage>(`/author/connections/${connectionId}/messages`, {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  getCalendar: () =>
    apiRequest<AuthorCalendarOverview>('/author/calendar', { channel: 'platform' }),
  createCalendarEvent: (body: Partial<AuthorCalendarEvent>) =>
    apiRequest<AuthorCalendarEvent>('/author/calendar/events', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  getTools: () =>
    apiRequest<AuthorResearchTool[]>('/author/tools', { channel: 'platform' }),
  runTool: (body: { tool_id: string; title: string; input_parameters?: Record<string, unknown> }) =>
    apiRequest<AuthorResearchToolArtifact>('/author/tools/run', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  getFiles: () =>
    apiRequest<AuthorStoredDocument[]>('/author/files', { channel: 'platform' }),
  uploadFile: (body: Partial<AuthorStoredDocument>) =>
    apiRequest<AuthorStoredDocument>('/author/files/upload', {
      method: 'POST',
      body,
      channel: 'platform',
    }),
  getJointArticles: () =>
    apiRequest<AuthorJointArticle[]>('/author/joint-articles', { channel: 'platform' }),
  aiQuery: (prompt: string, context_type?: string) =>
    apiRequest<AuthorAIResponse>('/author/ai/query', {
      method: 'POST',
      body: { prompt, context_type },
      channel: 'platform',
    }),
};


